import { AccountInfoState } from "../../account-info-header/models/AccountInfoState";
import { DraftListAttributes } from "../../initiate-and-release/Models/Drafts/DraftResponse";
import { EAuthInfo } from "../../shared/models/EAuthInfo";
import { HyphenatedPipe } from "../../shared/pipes/HyphenatedPipe";
import { TransactionsTableColumn } from "../../shared/types/TransactionTableColumn";
import { CheckDeliveryCodes, checkDeliveryCodesDict, payeeTypeDict, recipientAddressTypeDict, recipientType, StringBoolean, yesNo } from "../../shared/utilities/Constants";
import { StandingAuthCheckInfo } from "../../standing-authorizations/models/StandingAuthCheckInfo";
import { CheckStandingAuthDetails, StandingAuthorizationForm } from "../../standing-authorizations/models/standingAuthorizationForm";
import { PayeeDetails } from "../models/check/CheckData";
import { CheckInfo } from "../models/check/CheckInfo";
import { CheckTransactionDetails, TaxWithholdingFields, TransactionDetails } from "../models/form/TransactionForm";

export class CheckHelpers {
  private constructor() { }

  static accountDisplayRecord(accountInfo: AccountInfoState, hyphenate: boolean) {
    const hyphenatedPipe = new HyphenatedPipe();

    return {
      accountDisplayRecord: {
        masterAccount: hyphenate ? hyphenatedPipe.transform(accountInfo.masterAccount) : accountInfo.masterAccount,
        subAccountNumber: hyphenate ? hyphenatedPipe.transform(accountInfo.accountId) : accountInfo.accountId,
        subAccountNameLine1: accountInfo.subAccountNameLine1,
        subAccountNameLine2: accountInfo.subAccountNameLine2,
        subAccountNameLine3: accountInfo.subAccountNameLine3,
        subAccountNameLine1Title: accountInfo.subAccountNameLine1,
        regShortName: accountInfo.registrationType,
        investorChecking: false
      }
    }
  }

  static accountFields(accountInfo: AccountInfoState) {
    return {
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      IRAAccount: accountInfo.classifications.isIra,
    }
  }

  static acknowledgeFields(checkInfo: CheckInfo | StandingAuthCheckInfo, eAuthInfo: EAuthInfo, acknowledgementConsentDateTime: string) {
    return {
      eauthSelected: eAuthInfo.isAccountHolderSelected,
      EauthInd: checkInfo.eAuthModel.eauthInd,
      AnyAccountHolderIneligible: checkInfo.anyAccountHolderIneligible,
      AccountHolderIneligibleLog: checkInfo.accountHolderIneligibleLog,
      UseEAuthCheckDisableLog: checkInfo.useEauthCheckDisableLog,
      OrgAccountIndLog: false,
      CashieringIsCustAuthRequired: checkInfo.cashieringIsCustAuthRequired,
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      totalWithdrawalAmount: checkInfo.totalWithdrawalAmount,
      pendReasonsListSet: checkInfo.pendReasonsListSet,
      draftId: checkInfo.authResponse?.draftId ?? '',
    }
  }

  static addressFields(isThirdPartyRecipient: boolean, isInternational: boolean, transactionDetails: CheckTransactionDetails | CheckStandingAuthDetails, isUseSloa?: boolean, checkInfo?: CheckInfo) {
    let out = {
      otherDomesticmailTo1: '',
      otherDomesticmailTo2: '',
      otherDomesticmailTo3: '',
      otherDomesticstreetAddr1: '',
      otherDomesticstreetAddr2: '',
      otherDomesticAddrCity: '',
      otherDomesticAddrState: '',
      otherDomesticAddrZipCode: '',
      otherForeginmailTo1: '',
      otherForeginmailTo2: '',
      otherForeginmailTo3: '',
      otherForeginstreetAddr1: '',
      otherForeginstreetAddr2: '',
      otherForeginAddrCountry: '',
      otherForeginAddrCity: '',
      otherForeginAddrState: '',
      otherForegincAddrZipCode: ''
    }

    if (!isThirdPartyRecipient) {
      return out;
    }
    // I find nested if statements to be more readable than ternaries on every field
    else if (isInternational) {
      // Enforces type safety because isUseSloa and checkInfo are optional
      if (isUseSloa === true && checkInfo !== undefined) {
        out.otherForeginmailTo1 = checkInfo.mailAddressDetails[0]?.mailName ?? '';
        out.otherForeginmailTo2 = checkInfo.mailAddressDetails[1]?.mailName ?? '';
        out.otherForeginmailTo3 = checkInfo.mailAddressDetails[2]?.mailName ?? '';
        out.otherForeginstreetAddr1 = checkInfo.recipient.addressLine1?.toUpperCase() ?? '';
        out.otherForeginstreetAddr2 = checkInfo.recipient.addressLine2?.toUpperCase() ?? '';
        out.otherForeginAddrCountry = checkInfo.recipient.countryCode?.toUpperCase() ?? '';
        out.otherForeginAddrCity = checkInfo.recipient.city?.toUpperCase() ?? '';
        out.otherForeginAddrState = checkInfo.recipient.stateName?.toUpperCase() ?? '';
        out.otherForegincAddrZipCode = checkInfo.recipient.zipCode?.toUpperCase() ?? '';
      }
      else {
        out.otherForeginmailTo1 = transactionDetails.addresseeName?.toUpperCase() ?? '';
        out.otherForeginmailTo2 = transactionDetails.addresseeNameCont?.toUpperCase() ?? '';
        out.otherForeginmailTo3 = transactionDetails.addresseeNameCont2?.toUpperCase() ?? '';
        out.otherForeginstreetAddr1 = transactionDetails.streetAddress?.toUpperCase() ?? '';
        out.otherForeginstreetAddr2 = transactionDetails.streetAddressCont?.toUpperCase() ?? '';
        out.otherForeginAddrCountry = transactionDetails.country?.toUpperCase() ?? '';
        out.otherForeginAddrCity = transactionDetails.city?.toUpperCase() ?? '';
        out.otherForeginAddrState = transactionDetails.stateProvince?.toUpperCase() ?? '';
        out.otherForegincAddrZipCode = transactionDetails.postalCode?.toUpperCase() ?? '';
      }
      return out;
    }
    else {
      // Enforces type safety because isUseSloa and checkInfo are optional
      if (isUseSloa === true && checkInfo !== undefined) {
        out.otherDomesticmailTo1 = checkInfo.mailAddressDetails[0]?.mailName ?? '';
        out.otherDomesticmailTo2 = checkInfo.mailAddressDetails[1]?.mailName ?? '';
        out.otherDomesticmailTo3 = checkInfo.mailAddressDetails[2]?.mailName ?? '';
        out.otherDomesticstreetAddr1 = checkInfo.recipient.addressLine1?.toUpperCase() ?? '';
        out.otherDomesticstreetAddr2 = checkInfo.recipient.addressLine2?.toUpperCase() ?? '';
        out.otherDomesticAddrCity = checkInfo.recipient.city?.toUpperCase() ?? '';
        out.otherDomesticAddrState = checkInfo.recipient.stateCode?.toUpperCase() ?? '';
        out.otherDomesticAddrZipCode = checkInfo.recipient.zipCode?.toUpperCase() ?? '';
      }
      else {
        out.otherDomesticmailTo1 = transactionDetails.addresseeName?.toUpperCase() ?? '';
        out.otherDomesticmailTo2 = transactionDetails.addresseeNameCont?.toUpperCase() ?? '';
        out.otherDomesticmailTo3 = transactionDetails.addresseeNameCont2?.toUpperCase() ?? '';
        out.otherDomesticstreetAddr1 = transactionDetails.streetAddress?.toUpperCase() ?? '';
        out.otherDomesticstreetAddr2 = transactionDetails.streetAddressCont?.toUpperCase() ?? '';
        out.otherDomesticAddrCity = transactionDetails.city?.toUpperCase() ?? '';
        out.otherDomesticAddrState = transactionDetails.state?.toUpperCase() ?? '';
        out.otherDomesticAddrZipCode = transactionDetails.zipCode?.toUpperCase() ?? '';
      }
      return out;
    }
  }

  static amountFields(transactionDetails?: CheckTransactionDetails) {
    if (transactionDetails === undefined) {
      return {
        amount: '',
        amountType: 'gross',
      }
    }
    else {
      return {
        amount: transactionDetails.amount?.replace('$', '') ?? '',
        // Nullish coalescing to an empty string hasn't broken anything so far and feels safer
        amountType: transactionDetails.amountType ?? '',
      }
    }
  }

  static cashieringFields(isEdit: boolean, transactionData: TransactionsTableColumn | StandingAuthCheckInfo | undefined) {
    let out = {
      cashieringReqId: 0,
      cashieringSecondReqId: 0,
    }

    if (!isEdit || transactionData === undefined) {
      return out;
    }
    // Uses specific fields to determine which type we have
    else if ('CashieringRqstId' in transactionData) {
      out.cashieringReqId = parseInt(transactionData?.CashieringRqstId!);
      out.cashieringSecondReqId = parseInt(transactionData?.CashieringRqstSecondId!);
    }
    else if ('cashieringRequestId' in transactionData) {
      out.cashieringReqId = transactionData.cashieringRequestId;
      out.cashieringSecondReqId = transactionData.cashieringRequestSecondId;
    }
    return out;
  }

  static checkFields(checkInfo: CheckInfo | StandingAuthCheckInfo) {
    return {
      altrInd: checkInfo.isAlternateInvestmentUsage,
      altrText: checkInfo.alternateInvestmentText,
      accountPlatformCode: checkInfo.accountPlatformCode,
      defaultPayee: this.mapDefaultPayeeFrom(checkInfo.payeeDetails),
      // Since requestType only exists in CheckInfo we can use it to determine which type we have
      formType: ('requestType' in checkInfo ? checkInfo.requestType : checkInfo.type),
      isNonNAState: checkInfo.isNonNaState,
      recipient: checkInfo.recipient,
      useConstructedPayee: checkInfo.useConstructedPayee,
    }
  }

  static createStandingAuthFields(form: StandingAuthorizationForm, standingAuthorizationDetails: CheckStandingAuthDetails) {
    return {
      bsloatransaction: form.isSubmitAndCreateBundledSa ?? false,
      nickname: standingAuthorizationDetails.authorizationNickname,
    }
  }

  static distributionReasonFields(transactionData: TaxWithholdingFields | CheckStandingAuthDetails | undefined, checkInfo: CheckInfo | StandingAuthCheckInfo, transactionDetails?: CheckTransactionDetails) {
    return {
      distributionReasonCdValue: transactionData?.distributionReason ?? '',
      distributionReasonCd: checkInfo.distributionReasonsList?.find(element => element.description === transactionData?.distributionReason)?.code.toString() ?? transactionDetails?.distributionReasonCode ?? ' ',
      distributionReasonSubCd: transactionDetails?.distributionReasonSubCode ?? checkInfo.distributionReasonsList?.find(element => element.description === transactionData?.distributionReason)?.subCode.toString() ?? ' ',
    }
  }

  static feeDetail(checkInfo: CheckInfo | StandingAuthCheckInfo) {
    return {
      feeDetail: {
        chargedFee: 0,
        isFeePaidByFirm: false,
        deliveryFee: checkInfo.checkFeeDetails.defaultFee,
        waivedAmount: checkInfo.checkFeeDetails.qualFee,
      }
    }
  }

  static memoLine2Fields(transactionDetails: CheckTransactionDetails, checkInfo: CheckInfo | StandingAuthCheckInfo) {
    return {
      // Nullish coalescing to an empty string hasn't broken anything so far and it's easier to read this way, original below:
      // MemoLine2: transactionDetails.memoLine2 || transactionDetails.checkReason!.trim().length === 0 ? transactionDetails.memoLine2! : checkInfo.checkReasonMap.find(element => element.code === transactionDetails.checkReason!)?.description ?? '',
      MemoLine2: transactionDetails.memoLine2 ?? checkInfo.checkReasonMap.find(element => element.code === transactionDetails.checkReason!)?.description ?? '',
      MemoLine2DescriptionCode: transactionDetails.checkReason!,
    }
  }

  static payeeLineFields(transactionDetails: CheckTransactionDetails, checkInfo: CheckInfo | StandingAuthCheckInfo, isUseSloa: boolean, isEdit: boolean) {
    let out = {
      payeeLine1: '',
      payeeLine2: '',
      payeeLine3: ''
    }

    if (isUseSloa) {
      out.payeeLine1 = transactionDetails.defaultPayee?.[0] ?? '';
      out.payeeLine2 = transactionDetails.defaultPayee?.[1] ?? '';
      out.payeeLine3 = transactionDetails.defaultPayee?.[2] ?? '';
    }
    else if (transactionDetails.payeeType === recipientType.thirdParty) {
      out.payeeLine1 = transactionDetails.payeeName?.toUpperCase() ?? '';
      out.payeeLine2 = transactionDetails.additionalPayeeName1?.toUpperCase() ?? '';
      out.payeeLine3 = transactionDetails.additionalPayeeName2?.toUpperCase() ?? '';
    }
    else if (transactionDetails.modifiedPayeeCheckbox) {
      if (isEdit) {
        out.payeeLine1 = checkInfo.payeeDetails.payeeName1 ?? '';
        out.payeeLine2 = checkInfo.payeeDetails.payeeName2 ?? '';
        out.payeeLine3 = checkInfo.payeeDetails.payeeName3 ?? '';
      }
      else {
        out.payeeLine1 = transactionDetails.modifiedPayeeName1! ?? '';
        out.payeeLine2 = transactionDetails.modifiedPayeeName2! ?? '';
        out.payeeLine3 = transactionDetails.modifiedPayeeName3! ?? '';
      }
    }

    return out;
  }

  static processDateFields(processDateOrCheckInfo: Date | StandingAuthCheckInfo) {
    if (processDateOrCheckInfo instanceof Date) {
      return {
        processDateMM: processDateOrCheckInfo.getMonth() + 1,
        processDateDD: processDateOrCheckInfo.getDate(),
        processDateYYYY: processDateOrCheckInfo.getFullYear(),
      }
    }
    else {
      return {
        processDateMM: processDateOrCheckInfo.processDateMM,
        processDateDD: processDateOrCheckInfo.processDateDD,
        processDateYYYY: processDateOrCheckInfo.processDateYYYY,
      }
    }
  }

  static taxCheckFields(checkInfo: CheckInfo | StandingAuthCheckInfo) {
    let out = {
      fedTaxAmountFormatted: checkInfo.fedTaxAmountFormatted,
      stateTaxAmountFormatted: checkInfo.stateTaxAmountFormatted,
      stateTaxAttentionRequiredMI: 'false' as StringBoolean,
      stateTaxAttentionRequiredOther: 'false' as StringBoolean
    }

    if (checkInfo.stateTypeIndicator === 'S') {
      out.stateTaxAttentionRequiredOther = 'true' as StringBoolean;
      if (checkInfo.stateCd === 'MI') {
        out.stateTaxAttentionRequiredMI = 'true' as StringBoolean;
      }
    }

    return out;
  }

  static taxTransactionFields(transactionDetails: TaxWithholdingFields | CheckStandingAuthDetails | undefined) {
    let fedTaxNum = Number.parseFloat(transactionDetails?.fedTaxPercent ?? '');
    let stateTaxNum = Number.parseFloat(transactionDetails?.stateTaxPercent ?? '');

    return {
      fedTaxRtTWE: fedTaxNum,
      fedTaxAmount: fedTaxNum,
      withholdTaxes: fedTaxNum > 0 ? yesNo.yes : yesNo.no,

      stateTaxRtTWE: stateTaxNum,
      stateTaxAmount: stateTaxNum,
      withholdStateTaxes: stateTaxNum > 0 ? yesNo.yes : yesNo.no,
    }
  }

  static transactionFields(transactionDetails: CheckTransactionDetails | CheckStandingAuthDetails) {
    let deliveryCode = 'M'
    // Since deliveryMethod only exists in CheckTransactionDetails we can use it to determine which type we have
    if ('deliveryMethod' in transactionDetails) {
      deliveryCode = checkDeliveryCodesDict[transactionDetails.deliveryMethod as keyof typeof checkDeliveryCodesDict] ?? 'M';
    }
    return {
      DeliveryMethodCode: deliveryCode as CheckDeliveryCodes,
      isThirdParty: transactionDetails.payeeType !== 'firstParty',
      memo: transactionDetails.checkMemo!,
      payeeType: payeeTypeDict[transactionDetails.payeeType!] ?? '',
      recipientAddressType: recipientAddressTypeDict[transactionDetails.recipient!] ?? '',
    }
  }

  static verifyFields(InRTransactionInContext: DraftListAttributes) {
    return {
      draftId: InRTransactionInContext ? InRTransactionInContext.draftId : '',
    }
  }

  static withdrawlFields(transactionDetails: CheckTransactionDetails, checkInfo: CheckInfo | StandingAuthCheckInfo) {
    return {
      withdrawalReasonCode: transactionDetails.checkReason!,
      withdrawalReason: checkInfo.checkReasonMap.find(element => element.code === transactionDetails.checkReason!)?.description ?? '',
      totalWithdrawalAmount: checkInfo.totalWithdrawalAmount
    }
  }

  static mapDefaultPayeeFrom(checkInfoPayees: PayeeDetails): string[] {
    const payeeNames = [];

    if (checkInfoPayees?.payeeName1?.trim() ?? '' !== '') {
      payeeNames.push(checkInfoPayees.payeeName1.toUpperCase());
    }
    if (checkInfoPayees?.payeeName2?.trim() ?? '' !== '') {
      payeeNames.push(checkInfoPayees.payeeName2.toUpperCase());
    }
    if (checkInfoPayees?.payeeName3?.trim() ?? '' !== '') {
      payeeNames.push(checkInfoPayees.payeeName3.toUpperCase());
    }

    return payeeNames;
  }
}